
import React, { useState, useContext, useEffect } from 'react'
import {
    Button,
    Card,
    CardBody,
	CardHeader,
    Col,
    Form,
    FormGroup,
    Input,
    ListGroup,
    ListGroupItem,
	Label,
    Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
    Row
} from 'reactstrap'
import { useTranslation } from 'react-i18next'
import { SpinnerComponent } from 'react-element-spinner'
import dayjs from 'dayjs'

import { trigger } from '../../events/customEvents'
import AuctionsService from '../../services/AuctionsService'
import SiteService from '../../services/SiteService'
import { SessionContext } from '../../contexts/SessionProvider'
import Enumeration from '../../enumeration'

const CounterBidModal = ({ showCounterBidModal, auctionTierMemberBid, memberBidCounters, auctionTierExpiryInSeconds }) =>
{
	const [modalIsBusy, setModalIsBusy] = useState(false);
	const sessionContext = useContext(SessionContext)
	const { t } = useTranslation();
	const [counterBid, setCounterBid] = useState({
		auctionTierMemberBidId: auctionTierMemberBid?.auctionTierMemberBidId,
		counterBidAmount: undefined,
		note: '',
		parentBidTypeId: undefined,
		parentEntityId: undefined,
		validation: { counterBidAmount: false, note: true }
	});
	const [counterBidSubmitted, setCounterBidSubmitted] = useState(false)
    const [openConfirmAcceptCounterModal, setOpenConfirmAcceptCounterModal] = useState(false)
    const [currentMemberBidCounter, setCurrentMemberBidCounter] = useState(undefined)
	const [minimumAllowableCounterBidAmount, setMinimumAllowableCounterBidAmount] = useState(undefined)

	useEffect(() =>
	{
	
	}, [])


	const handleOnOpened = () =>
	{
		const parentBidTypeId = (memberBidCounters && memberBidCounters.length > 0) ? Enumeration.ParentBidTypes.Counter : Enumeration.ParentBidTypes.Bid
		const parentEntityId = (memberBidCounters && memberBidCounters.length > 0) ? memberBidCounters[memberBidCounters.length - 1].auctionTierMemberBidCounterId : auctionTierMemberBid.bidItemEntityId
		
		setCounterBid({
			auctionTierMemberBidId: (auctionTierMemberBid.hasOwnProperty('auctionTierMemberBidId')) ? auctionTierMemberBid?.auctionTierMemberBidId : auctionTierMemberBid?.bidItemEntityId,
			counterBidAmount: undefined,
			note: '',
			parentBidTypeId: parentBidTypeId,
			parentEntityId: parentEntityId,
			validation: {counterBidAmount: false, note: true}
		})
		
		setMinimumAllowableCounterBidAmount(auctionTierMemberBid.bidAmount + 100)
		if (memberBidCounters !== undefined && memberBidCounters.length > 0)
		{
			setMinimumAllowableCounterBidAmount(memberBidCounters[memberBidCounters.length - 1].counterBidAmount + 100)
		}
	}

	const handleCancelModal = (e) =>
	{
		e.preventDefault()
		trigger('counterMemberBidModalClosed', { counterPlaced: false, reason: 'Cancelled', counterBidAmount: 0 })
	}

	const handleCounterBidPropertyChange = (e) =>
	{
		const { name, value} = e.target
		setCounterBid((previousCounterBid) =>
		{
			const currentCounterBid = { ...previousCounterBid }
			currentCounterBid[name] = value
			switch (name)
			{
				case 'counterBidAmount':
					currentCounterBid.validation[name] = (!isNaN(value) && (value > 0))
					break
				case 'note':

					break;
			}
			return currentCounterBid
		})
	}

	const isValidCounter = () =>
	{
		const counterBidValidation =
		{
			isValid: false,
			validationMessage: ''
		}
		
		const numberFormat = new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
		});
		
		counterBidValidation.isValid = (counterBid.validation.counterBidAmount && counterBid.validation.note) && (counterBid.counterBidAmount !== undefined && counterBid.counterBidAmount >= minimumAllowableCounterBidAmount)
		counterBidValidation.validationMessage = (counterBidValidation.isValid) ? '' : `Invalid input. The counter bid amount must be at least ${numberFormat.format(minimumAllowableCounterBidAmount)}`
		
		return counterBidValidation
	}

	const handlePlaceCounter = (e) =>
	{
		e.preventDefault()
		
		setCounterBidSubmitted(true)
		const counterBidValidation = isValidCounter()
		if (counterBidValidation.isValid)
		{
			setModalIsBusy(true)
			AuctionsService
				.addMemberBidCounter(counterBid, sessionContext.loginSession.user.userSession.sessionKey)
				.then(result =>
				{
					setModalIsBusy(false)
					sessionContext.updateSession(result.data.loginSession)

					trigger('raiseNotification', { title: 'Add Success', message: 'The counter bid was placed.', type: 'success', duration: 5 })
					trigger('counterMemberBidModalClosed', { counterPlaced: true, reason: '', counterBidAmount: counterBid.counterBidAmount })
				})
				.catch((error) =>
				{
					setModalIsBusy(false)
					SiteService.displayApiError(error)
				})
		}
		else
		{
			setCounterBidSubmitted(false)
			SiteService.displayApiError({ message: counterBidValidation.validationMessage })
		}
	}

	const handleDeclineCounter = (memberBidCounter) =>
	{
		const auctionTierMemberBidCounterId = memberBidCounter.auctionTierMemberBidCounterId

		setModalIsBusy(true)
		AuctionsService
			.declineCounterBid(auctionTierMemberBid.auctionTierMemberBidId, auctionTierMemberBidCounterId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setModalIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)

				trigger('raiseNotification', { title: 'Decline Success', message: 'The counter bid was declined.', type: 'success', duration: 5 })
				trigger('counterMemberBidModalClosed', { counterDeclined: true })
			})
			.catch((error) =>
			{
				setModalIsBusy(false)
				SiteService.displayApiError(error)
			})
	}

    const confirmAcceptCounter = (memberBidCounter) =>
    {
        setCurrentMemberBidCounter(memberBidCounter)
        setOpenConfirmAcceptCounterModal(true)
    }

	const handleAcceptCounter = () =>
	{
        setOpenConfirmAcceptCounterModal(false)

		const auctionTierMemberBidCounterId = currentMemberBidCounter.auctionTierMemberBidCounterId

		setModalIsBusy(true)
		AuctionsService
			.acceptCounterBid(auctionTierMemberBid.auctionTierMemberBidId, auctionTierMemberBidCounterId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setModalIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)

				trigger('raiseNotification', { title: 'Accept Success', message: 'The counter bid was accepted.', type: 'success', duration: 5 })
				trigger('counterMemberBidModalClosed', { counterDeclined: false })
			})
			.catch((error) =>
			{
				setModalIsBusy(false)
				SiteService.displayApiError(error)
			})
	}

	const getUserGroupName = (userGroupId) =>
	{
		let userGroupName = ''
		switch (userGroupId)
		{
			case Enumeration.UserGroups.Administrators:
				userGroupName = 'Administrator'
				break
			case Enumeration.UserGroups.Dealers:
				userGroupName = 'Dealer'
				break
		}
		return userGroupName
	}
	
	const counterBidStatusLabel = (memberBidCounter) =>
	{
		let statusLabel = ''
		// dayjs(bidItem.bidTime).format('MMM DD, YYYY HH:mm')
		if (memberBidCounter.accepted)
		{
			statusLabel = `Accepted on ${ dayjs(memberBidCounter.acceptedTime).format('MMM D, YYYY') } at ${ dayjs(memberBidCounter.acceptedTime).format('HH:mm') }`
		}
		if (memberBidCounter.declined)
		{
			statusLabel = `Declined on ${ dayjs(memberBidCounter.declinedTime).format('MMM D, YYYY') } at ${ dayjs(memberBidCounter.declinedTime).format('HH:mm') }`
		}
		return statusLabel
	}

	// QA Feedback: When CDLSI counters a dealer's high bid, the dealer can only
	// accept or decline our counter — they cannot counter back. Hide the place
	// counter bid input and the footer counter button for dealer users.
	const currentUserGroupId = sessionContext?.loginSession?.user?.usersRoleMemberships?.[0]?.userRole?.userGroupId
	const isDealer = currentUserGroupId === Enumeration.UserGroups.Dealers

	return (
		<>
			<Modal
				size="lg"
				fullscreen="md"
				isOpen={ showCounterBidModal }
				onOpened={ handleOnOpened }
				toggle={ handleCancelModal }
			>
				<SpinnerComponent
					loading={ modalIsBusy }
					position="centered"
					color="#3578e5"
					secondaryColor="#f3f3f3"
					backgroundColor="#d3d3d3"
					spinnerType="circle-dots"
				/>
				<ModalHeader toggle={ handleCancelModal } charCode="Y">
					{t('Modals.CounterMemberBid.Title')}
				</ModalHeader>
				<ModalBody>
					{
						(auctionTierMemberBid !== undefined) &&
						// Top Bid section starts
						<Card style={{ marginBottom: '10px' }}>
							<CardHeader style={{ height: '2rem' }} className="text-left text-muted mb-0 text-black-50">
								{ t('Modals.CounterMemberBid.Bid') }
							</CardHeader>
							<CardBody>
								<ListGroup flush>
									<ListGroupItem style={{ padding: '0' }}>
										<Row className="align-items-center">
											<Col>
												<Label>
													<a href="#" onClick={(e) => e.preventDefault()}>
														{ `${ auctionTierMemberBid.fullName } (${ auctionTierMemberBid.dealerName })` }
													</a>
												</Label>
												<div>
													<small>Bid Time:</small>{': '}
													<small>{ (auctionTierMemberBid.bidTime !== null) ? dayjs(auctionTierMemberBid.bidTime).format('MMM DD, YYYY HH:mm') : '' }</small>
												</div>
												<div>
													<small>Bid Amount:</small>{': '}
													<span className="text-success"><small>{ auctionTierMemberBid.bidAmount ? auctionTierMemberBid.bidAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 'N/A' }</small></span>
												</div>
											</Col>
										</Row>
									</ListGroupItem>
								</ListGroup>
							</CardBody>
						</Card>
					}
					{
						(memberBidCounters.length > 0) &&
						// Pending current bid section starts
						<Card>
							<CardHeader style={{ height: '2rem' }} className="text-left text-muted mb-0 text-black-50">
								{ t('Modals.CounterMemberBid.Pending') }
							</CardHeader>
							<CardBody>
								{
									memberBidCounters.map((memberBidCounter, idx) => (
										<ListGroup key={ idx } flush>
											<ListGroupItem style={{ padding: '0' }}>
												<table style={{ width: '100%' }} >
													<tr>
														<td colspan="3">
															<small>{ t('Modals.CounterMemberBid.Counter_By') }</small>{': '}
															<span className="text-nowrap"><small>{ getUserGroupName(memberBidCounter.counterBidByUserGroupId) }</small></span>
														</td>
													</tr>
													<tr>
														<td>
															<small>{ t('Modals.CounterMemberBid.Counter_Amount') }</small>{': '}
															<span className="text-nowrap"><small>{ memberBidCounter.counterBidAmount ? memberBidCounter.counterBidAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 'N/A' }</small></span>
														</td>
														<td style={{ width: '25px' }}><nbsp /></td>
														<td>
															<small>{ t('Modals.CounterMemberBid.Counter_Expiry') }</small>{': '}
															<span className="text-nowrap"><small>{ SiteService.dateTimeDisplayEastern(memberBidCounter.counterUtcExpiryTime) }</small></span>
														</td>
													</tr>
													<tr>
														<td>
															<small>{ t('Modals.CounterMemberBid.Note') }</small>{': '}
														</td>
													</tr>
													<tr>
														<td colspan="3">
															<p>
																<small>{memberBidCounter.note }</small>
															</p>
														</td>
													</tr>
													{
														((memberBidCounter.counterBidByUserGroupId !== sessionContext.loginSession.user.usersRoleMemberships[0].userRole.userGroupId) &&
															(!memberBidCounter.accepted && !memberBidCounter.declined) &&
															(idx === memberBidCounters.length - 1)) &&
														<tr>
															<td style={{ paddingTop: '10px' }}>
																	<Button
																		block
																		color="primary"
																		size="sm"
																		onClick={() => confirmAcceptCounter(memberBidCounter)}
																		disabled={ memberBidCounter.counterExpiryInSeconds < 1 }
																		style={{ marginRight: '10px' }}
																	>
																		{ t('Modals.CounterMemberBid.Accept') }
																	</Button>
															</td>
															<td></td>
															<td style={{ paddingTop: '10px' }}>
																	<Button
																		block
																		color="warning"
																		size="sm"
																		onClick={() => handleDeclineCounter(memberBidCounter)}
																		disabled={ memberBidCounter.counterExpiryInSeconds < 1 }
																	>
																		{ t('Modals.CounterMemberBid.Decline') }
																	</Button>
																
															</td>
														</tr>
													}
													{
														((memberBidCounter.counterBidByUserGroupId !== sessionContext.loginSession.user.usersRoleMemberships[0].userRole.userGroupId) &&
															(!memberBidCounter.accepted || !memberBidCounter.declined) &&
															(idx === memberBidCounters.length - 1)) &&
														<tr>
															<td colSpan="3">
																<div >
																	<label style={{ marginBottom: 0, paddingBottom: 0 }} className="form-control-label mr-2">
																		{ counterBidStatusLabel(memberBidCounter) }
																	</label>
																</div>
															</td>
														</tr>
													}
													<tr><td colspan={3}><hr style={{ marginTop: '3px', marginBottom: '3px' }} /></td></tr>
												</table>
											</ListGroupItem>
										</ListGroup>
									))
								}
							</CardBody>
						</Card>
					}
					{
						// QA Feedback: dealers cannot counter CDLSI's counter; hide the
						// place counter bid input for dealer users (accept/decline only).
						!isDealer &&
						<Card>
							<CardHeader style={{ height: '2rem' }} className="text-left text-muted mb-0 text-black-50">
								{ t('Modals.CounterMemberBid.Counter') }
							</CardHeader>
							<CardBody>
								<Col md="12" className="text-primary" >
									<Form role="form">
										<FormGroup>
											<Input
												className="form-control-sm"
												type="text"
												name="counterBidAmount"
												placeholder={t('Modals.CounterMemberBid.Counter_Bid_Amount')}
												value={counterBid.counterBidAmount}
												onChange={handleCounterBidPropertyChange}
												valid={counterBidSubmitted && counterBid.validation.counterBidAmount}
												invalid={counterBidSubmitted && !counterBid.validation.counterBidAmount}
											/>
										</FormGroup>
										<FormGroup>
											<Input
												className="form-control-sm"
												type="textarea"
												name="note"
												placeholder={t('Modals.CounterMemberBid.Note')}
												value={counterBid.note}
												onChange={handleCounterBidPropertyChange}
												valid={counterBidSubmitted && counterBid.validation.note}
												invalid={counterBidSubmitted && !counterBid.validation.note}
											/>
										</FormGroup>
									</Form>
								</Col>
							</CardBody>
						</Card>
					}
				</ModalBody>
				<ModalFooter>
					<Row style={{ width: '100%' }}>
						<Col md={ isDealer ? 12 : 6 }>
							<Button
								block
								color="secondary"
								type="button"
								size="sm"
								onClick={ handleCancelModal }
							>
								{t('Modals.PlaceMemberBid.Cancel')}
							</Button>
						</Col>
						{
							!isDealer &&
							<Col md={6}>
								<Button
									block
									color="primary"
									type="button"
									size="sm"
									onClick={ handlePlaceCounter }
									disabled={ auctionTierExpiryInSeconds < 1 }
								>
									{t('Modals.CounterMemberBid.Counter')}
								</Button>
							</Col>
						}
					</Row>
				</ModalFooter>
			</Modal>
			
			<Modal
				isOpen={ openConfirmAcceptCounterModal }
				toggle={ () => setOpenConfirmAcceptCounterModal(false) }
			>
				<ModalHeader toggle={ () => setOpenConfirmAcceptCounterModal(false) } charCode="Y">
					{ t('Modals.CounterMemberBid.Accept') }
				</ModalHeader>
				<ModalBody>
					<Card>
						<CardBody>
							<div><p>{t('Modals.CounterMemberBid.Accept_Prompt')}</p></div>
						</CardBody>
					</Card>
				</ModalBody>
				<ModalFooter>
					<Row style={{ width: '100%' }}>
						<Col md={6}>
							<Button
								block
								color="secondary"
								className="btn-block"
								onClick={ () => setOpenConfirmAcceptCounterModal(false) }
							>
								{ t('Common.No') }
							</Button>
						</Col>
						<Col md={6}>
							<Button
								block
								color="primary"
								className="btn-block"
								onClick={ handleAcceptCounter }
							>
								{ t('Common.Yes') }
							</Button>
						</Col>
					</Row>
				</ModalFooter>
			</Modal>
		</>
	)
}

export default CounterBidModal
